import { Controller } from '@hotwired/stimulus';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BankField {
    key: string;
    label: string;
    hint?: string;
    placeholder?: string;
    maxlength?: number;
    inputmode?: string;
    type?: 'select';
    options?: { value: string; label: string }[];
    required?: boolean;
}

interface CountryConfig {
    hint: string;
    fields: BankField[];
    validate?: (v: Record<string, string>) => { ok: boolean; msg: string };
    toEntity: (v: Record<string, string>) => Partial<{
        bankName: string; accountHolder: string; accountNumber: string;
        accountType: string; swiftBic: string;
    }>;
    fromEntity: (e: Record<string, string | null>) => Record<string, string>;
}

// ─── Card helpers ─────────────────────────────────────────────────────────────

const CARD_BRANDS: [RegExp, string][] = [
    [/^4/,               'VISA'],
    [/^5[1-5]|^2[2-7]/,  'Mastercard'],
    [/^3[47]/,           'Amex'],
    [/^6(?:011|5)/,      'Discover'],
    [/^3(?:0[0-5]|[68])/, 'Diners'],
    [/^35/,              'JCB'],
];

function luhn(num: string): boolean {
    const d = num.replace(/\D/g, '');
    let sum = 0, alt = false;
    for (let i = d.length - 1; i >= 0; i--) {
        let n = +d[i];
        if (alt) { n *= 2; if (n > 9) n -= 9; }
        sum += n; alt = !alt;
    }
    return sum % 10 === 0 && d.length > 0;
}

function formatCard(raw: string, isAmex: boolean): string {
    const d = raw.replace(/\D/g, '').slice(0, isAmex ? 15 : 19);
    if (isAmex) return (d.slice(0, 4) + ' ' + d.slice(4, 10) + ' ' + d.slice(10)).trim();
    return d.replace(/(.{4})(?=.)/g, '$1 ');
}

// ─── IBAN mod-97 ──────────────────────────────────────────────────────────────

function validateIban(iban: string): { ok: boolean; msg: string } {
    const raw = iban.replace(/[\s-]/g, '').toUpperCase();
    if (raw.length < 5) return { ok: false, msg: 'IBAN demasiado corto' };
    const rearr   = raw.slice(4) + raw.slice(0, 4);
    const numeric = rearr.split('').map(c => {
        const n = c.charCodeAt(0) - 55;
        return (n >= 10 && n <= 35) ? String(n) : c;
    }).join('');
    let rem = 0;
    for (const ch of numeric) rem = (rem * 10 + +ch) % 97;
    return rem === 1
        ? { ok: true,  msg: 'IBAN válido ✓' }
        : { ok: false, msg: 'IBAN inválido (mod 97 falló)' };
}

// ─── Country schemas ──────────────────────────────────────────────────────────

const COUNTRIES: Record<string, CountryConfig> = {
    MX: {
        hint: 'CLABE interbancaria: 18 dígitos — banco (3) + ciudad (3) + cuenta (11) + dígito de control (1)',
        fields: [
            { key: 'clabe',  label: 'CLABE interbancaria', placeholder: '18 dígitos', maxlength: 18, inputmode: 'numeric', hint: '18 dígitos', required: true },
            { key: 'clabeConfirm', label: 'Confirmar CLABE', placeholder: '18 dígitos', maxlength: 18, inputmode: 'numeric', required: true },
            { key: 'holder', label: 'Titular de la cuenta', required: true },
        ],
        validate(v) {
            if (v.clabe !== v.clabeConfirm) return { ok: false, msg: 'Las CLABEs no coinciden' };
            const d = (v.clabe ?? '').replace(/\D/g, '');
            if (d.length !== 18) return { ok: false, msg: `La CLABE debe tener 18 dígitos (tiene ${d.length})` };
            const w = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
            const sum = w.reduce((acc, wi, i) => acc + wi * +d[i], 0);
            const ctrl = (10 - (sum % 10)) % 10;
            return ctrl === +d[17]
                ? { ok: true,  msg: 'CLABE válida ✓' }
                : { ok: false, msg: `Dígito de control incorrecto (esperado ${ctrl})` };
        },
        toEntity:   v => ({ accountNumber: v.clabe, accountHolder: v.holder }),
        fromEntity: e => ({ clabe: e.accountNumber ?? '', clabeConfirm: e.accountNumber ?? '', holder: e.accountHolder ?? '' }),
    },
    US: {
        hint: 'ABA Routing Number (9 dígitos) + Account Number (4-17 dígitos)',
        fields: [
            { key: 'routing',     label: 'Routing number (ABA)', placeholder: '9 dígitos',    maxlength: 9,  inputmode: 'numeric', hint: '9 dígitos',    required: true },
            { key: 'account',     label: 'Account number',       placeholder: '4-17 dígitos',  maxlength: 17, inputmode: 'numeric', hint: '4-17 dígitos', required: true },
            { key: 'accountConfirm', label: 'Confirmar Account number', placeholder: '4-17 dígitos', maxlength: 17, inputmode: 'numeric', required: true },
            { key: 'accountType', label: 'Account type', type: 'select', options: [{ value: 'corriente', label: 'Checking' }, { value: 'ahorros', label: 'Savings' }], required: true },
            { key: 'holder',      label: 'Account holder', required: true },
        ],
        validate(v) {
            if (v.account !== v.accountConfirm) return { ok: false, msg: 'Los números de cuenta no coinciden' };
            const d = (v.routing ?? '').replace(/\D/g, '');
            if (d.length !== 9) return { ok: false, msg: 'El ABA debe tener 9 dígitos' };
            const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
            const sum = w.reduce((acc, wi, i) => acc + wi * +d[i], 0);
            return sum % 10 === 0 ? { ok: true, msg: 'ABA válido ✓' } : { ok: false, msg: 'ABA inválido (checksum incorrecto)' };
        },
        toEntity:   v => ({ swiftBic: v.routing, accountNumber: v.account, accountType: v.accountType, accountHolder: v.holder }),
        fromEntity: e => ({ routing: e.swiftBic ?? '', account: e.accountNumber ?? '', accountConfirm: e.accountNumber ?? '', accountType: e.accountType ?? '', holder: e.accountHolder ?? '' }),
    },
    BR: {
        hint: 'Código de banco (3) + agência (4) + número de conta (variable) + dígito verificador',
        fields: [
            { key: 'bankCode',    label: 'Código do banco',    placeholder: 'ex. 001',     maxlength: 3,  inputmode: 'numeric', hint: '3 dígitos',    required: true },
            { key: 'agencia',     label: 'Agência',            placeholder: '4 dígitos',   maxlength: 4,  inputmode: 'numeric', hint: '4 dígitos',    required: true },
            { key: 'account',     label: 'Número da conta',    placeholder: '5-13 dígitos',               hint: '5-13 caracteres', required: true },
            { key: 'accountConfirm', label: 'Confirmar número da conta', placeholder: '5-13 dígitos', required: true },
            { key: 'digit',       label: 'Dígito verificador', placeholder: '1 dígito',    maxlength: 1,  inputmode: 'numeric', hint: '1 dígito',     required: true },
            { key: 'accountType', label: 'Tipo de conta', type: 'select', options: [{ value: 'corriente', label: 'Conta corrente' }, { value: 'ahorros', label: 'Conta poupança' }], required: true },
            { key: 'cpfCnpj',    label: 'CPF / CNPJ do titular', placeholder: '11 ou 14 dígitos', hint: '11-14 caracteres', required: true },
            { key: 'holder',      label: 'Nome do titular', required: true },
        ],
        validate(v) {
            if (v.account !== v.accountConfirm) return { ok: false, msg: 'Os números de conta não coincidem' };
            return { ok: true, msg: 'Conta válida ✓' };
        },
        toEntity(v) {
            return {
                bankName:      v.bankCode,
                swiftBic:      v.agencia,
                accountNumber: v.account + (v.digit ? '-' + v.digit : ''),
                accountType:   v.accountType,
                accountHolder: (v.holder ?? '') + (v.cpfCnpj ? ' / ' + v.cpfCnpj : ''),
            };
        },
        fromEntity(e) {
            const [account = '', digit = ''] = (e.accountNumber ?? '').split('-');
            const [holder = '', cpfCnpj = ''] = (e.accountHolder ?? '').split(' / ');
            return { bankCode: e.bankName ?? '', agencia: e.swiftBic ?? '', account, accountConfirm: account, digit, accountType: e.accountType ?? '', cpfCnpj, holder };
        },
    },
    ES: {
        hint: 'IBAN español: ES + 22 caracteres (mod 97). BIC/SWIFT opcional.',
        fields: [
            { key: 'iban',     label: 'IBAN',           placeholder: 'ES00 0000 0000 0000 0000 0000', maxlength: 34, hint: 'ES + 22 caracteres', required: true },
            { key: 'ibanConfirm', label: 'Confirmar IBAN',  placeholder: 'ES00 0000 0000 0000 0000 0000', maxlength: 34, required: true },
            { key: 'bic',      label: 'BIC / SWIFT',    placeholder: 'XXXXESMMXXX', maxlength: 11, hint: 'Opcional' },
            { key: 'bankName', label: 'Entidad bancaria', hint: 'Opcional' },
            { key: 'holder',   label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.iban !== v.ibanConfirm) return { ok: false, msg: 'Los IBAN no coinciden' };
            return validateIban(v.iban ?? '');
        },
        toEntity:   v => ({ accountNumber: v.iban, swiftBic: v.bic, bankName: v.bankName, accountHolder: v.holder }),
        fromEntity: e => ({ iban: e.accountNumber ?? '', ibanConfirm: e.accountNumber ?? '', bic: e.swiftBic ?? '', bankName: e.bankName ?? '', holder: e.accountHolder ?? '' }),
    },
    DE: {
        hint: 'IBAN alemán: DE + 20 caracteres (mod 97). BIC/SWIFT opcional.',
        fields: [
            { key: 'iban',   label: 'IBAN',        placeholder: 'DE00 0000 0000 0000 0000 00', maxlength: 34, hint: 'DE + 20 caracteres', required: true },
            { key: 'ibanConfirm', label: 'Confirmar IBAN', placeholder: 'DE00 0000 0000 0000 0000 00', maxlength: 34, required: true },
            { key: 'bic',    label: 'BIC / SWIFT', placeholder: 'XXXXDEMMXXX', maxlength: 11, hint: 'Opcional' },
            { key: 'holder', label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.iban !== v.ibanConfirm) return { ok: false, msg: 'Los IBAN no coinciden' };
            return validateIban(v.iban ?? '');
        },
        toEntity:   v => ({ accountNumber: v.iban, swiftBic: v.bic, accountHolder: v.holder }),
        fromEntity: e => ({ iban: e.accountNumber ?? '', ibanConfirm: e.accountNumber ?? '', bic: e.swiftBic ?? '', holder: e.accountHolder ?? '' }),
    },
    CO: {
        hint: 'Colombia: nombre del banco + número de cuenta + tipo',
        fields: [
            { key: 'bankName',    label: 'Banco',            placeholder: 'Nombre del banco', required: true },
            { key: 'account',     label: 'Número de cuenta', placeholder: 'Número de cuenta', required: true },
            { key: 'accountConfirm', label: 'Confirmar número de cuenta', placeholder: 'Número de cuenta', required: true },
            { key: 'accountType', label: 'Tipo de cuenta', type: 'select', options: [{ value: 'corriente', label: 'Corriente' }, { value: 'ahorros', label: 'Ahorros' }], required: true },
            { key: 'holder',      label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.account !== v.accountConfirm) return { ok: false, msg: 'Los números de cuenta no coinciden' };
            return { ok: true, msg: 'Cuenta válida ✓' };
        },
        toEntity:   v => ({ bankName: v.bankName, accountNumber: v.account, accountType: v.accountType, accountHolder: v.holder }),
        fromEntity: e => ({ bankName: e.bankName ?? '', account: e.accountNumber ?? '', accountConfirm: e.accountNumber ?? '', accountType: e.accountType ?? '', holder: e.accountHolder ?? '' }),
    },
    AR: {
        hint: 'CBU: 22 dígitos — entidad (3) + sucursal (4) + dígito (1) + cuenta (13) + dígito (1)',
        fields: [
            { key: 'bankName', label: 'Banco', placeholder: 'Nombre del banco', required: true },
            { key: 'cbu',    label: 'CBU',     placeholder: '22 dígitos', maxlength: 22, inputmode: 'numeric', hint: '22 dígitos', required: true },
            { key: 'cbuConfirm', label: 'Confirmar CBU', placeholder: '22 dígitos', maxlength: 22, inputmode: 'numeric', required: true },
            { key: 'holder', label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.cbu !== v.cbuConfirm) return { ok: false, msg: 'Los CBU no coinciden' };
            const d = (v.cbu ?? '').replace(/\D/g, '');
            return d.length === 22 ? { ok: true, msg: 'CBU válido ✓' } : { ok: false, msg: `El CBU debe tener 22 dígitos (tiene ${d.length})` };
        },
        toEntity:   v => ({ accountNumber: v.cbu, accountHolder: v.holder, bankName: v.bankName }),
        fromEntity: e => ({ cbu: e.accountNumber ?? '', cbuConfirm: e.accountNumber ?? '', holder: e.accountHolder ?? '', bankName: e.bankName ?? '' }),
    },
    PE: {
        hint: 'CCI (Código de Cuenta Interbancario): 20 dígitos',
        fields: [
            { key: 'cci',     label: 'CCI',             placeholder: '20 dígitos',  maxlength: 20, inputmode: 'numeric', hint: '20 dígitos', required: true },
            { key: 'cciConfirm', label: 'Confirmar CCI', placeholder: '20 dígitos', maxlength: 20, inputmode: 'numeric', required: true },
            { key: 'account', label: 'Número de cuenta', placeholder: 'Opcional', required: false },
            { key: 'holder',  label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.cci !== v.cciConfirm) return { ok: false, msg: 'Los CCI no coinciden' };
            const d = (v.cci ?? '').replace(/\D/g, '');
            return d.length === 20 ? { ok: true, msg: 'CCI válido ✓' } : { ok: false, msg: `El CCI debe tener 20 dígitos (tiene ${d.length})` };
        },
        toEntity:   v => ({ accountNumber: v.cci, swiftBic: v.account, accountHolder: v.holder }),
        fromEntity: e => ({ cci: e.accountNumber ?? '', cciConfirm: e.accountNumber ?? '', account: e.swiftBic ?? '', holder: e.accountHolder ?? '' }),
    },
    OTHER: {
        hint: 'IBAN internacional (mod 97) + BIC/SWIFT',
        fields: [
            { key: 'iban',     label: 'IBAN',        placeholder: 'XX00 0000 0000 0000 …', maxlength: 34, required: true },
            { key: 'ibanConfirm', label: 'Confirmar IBAN', placeholder: 'XX00 0000 0000 0000 …', maxlength: 34, required: true },
            { key: 'bic',      label: 'BIC / SWIFT', placeholder: 'XXXXXXXXXX', maxlength: 11, hint: 'Opcional' },
            { key: 'bankName', label: 'Banco',       hint: 'Opcional' },
            { key: 'holder',   label: 'Titular', required: true },
        ],
        validate(v) {
            if (v.iban !== v.ibanConfirm) return { ok: false, msg: 'Los IBAN no coinciden' };
            return validateIban(v.iban ?? '');
        },
        toEntity:   v => ({ accountNumber: v.iban, swiftBic: v.bic, bankName: v.bankName, accountHolder: v.holder }),
        fromEntity: e => ({ iban: e.accountNumber ?? '', ibanConfirm: e.accountNumber ?? '', bic: e.swiftBic ?? '', bankName: e.bankName ?? '', holder: e.accountHolder ?? '' }),
    },
};

// ─── Controller ───────────────────────────────────────────────────────────────

export default class DatosBancariosController extends Controller {
    static targets = [
        'tabCuenta', 'tabTarjeta',
        'cuentaSection', 'tarjetaSection',
        'cardPreviewNumber', 'cardPreviewBrand', 'cardPreviewHolder', 'cardLuhnStatus',
        'cardNumberInput', 'cardNumberConfirmInput', 'cardExpiryInput', 'cardCvvInput', 'cardHolderInput',
        'validateCardBtn',
        'countrySelect', 'countryHint', 'bankFieldsContainer', 'validateAccountBtn',
        'fPaymentType', 'fCountry',
        'fBankName', 'fAccountHolder', 'fAccountNumber', 'fAccountNumberConfirm', 'fAccountType', 'fSwiftBic',
        'fCardNumber', 'fCardNumberConfirm', 'fCardExpiry', 'fCardCvv', 'fCardHolder', 'fCardBrand',
    ];

    declare readonly tabCuentaTarget: HTMLButtonElement;
    declare readonly tabTarjetaTarget: HTMLButtonElement;
    declare readonly cuentaSectionTarget: HTMLElement;
    declare readonly tarjetaSectionTarget: HTMLElement;
    declare readonly cardPreviewNumberTarget: HTMLElement;
    declare readonly cardPreviewBrandTarget: HTMLElement;
    declare readonly cardPreviewHolderTarget: HTMLElement;
    declare readonly cardLuhnStatusTarget: HTMLElement;
    declare readonly cardNumberInputTarget: HTMLInputElement;
    declare readonly cardNumberConfirmInputTarget: HTMLInputElement;
    declare readonly cardExpiryInputTarget: HTMLInputElement;
    declare readonly cardCvvInputTarget: HTMLInputElement;
    declare readonly cardHolderInputTarget: HTMLInputElement;
    declare readonly validateCardBtnTarget: HTMLButtonElement;
    declare readonly countrySelectTarget: HTMLSelectElement;
    declare readonly countryHintTarget: HTMLElement;
    declare readonly bankFieldsContainerTarget: HTMLElement;
    declare readonly validateAccountBtnTarget: HTMLButtonElement;
    declare readonly fPaymentTypeTarget: HTMLInputElement;
    declare readonly fCountryTarget: HTMLInputElement;
    declare readonly fBankNameTarget: HTMLInputElement;
    declare readonly fAccountHolderTarget: HTMLInputElement;
    declare readonly fAccountNumberTarget: HTMLInputElement;
    declare readonly fAccountNumberConfirmTarget: HTMLInputElement;
    declare readonly fAccountTypeTarget: HTMLInputElement;
    declare readonly fSwiftBicTarget: HTMLInputElement;
    declare readonly fCardNumberTarget: HTMLInputElement;
    declare readonly fCardNumberConfirmTarget: HTMLInputElement;
    declare readonly fCardExpiryTarget: HTMLInputElement;
    declare readonly fCardCvvTarget: HTMLInputElement;
    declare readonly fCardHolderTarget: HTMLInputElement;
    declare readonly fCardBrandTarget: HTMLInputElement;

    private brand   = '';
    private country = '';
    private boundBankSync!: (e: Event) => void;

    connect(): void {
        // Reactive sync: bank fields container delegates input/change events
        this.boundBankSync = this.syncBankToHidden.bind(this);
        this.bankFieldsContainerTarget.addEventListener('input',  this.boundBankSync);
        this.bankFieldsContainerTarget.addEventListener('change', this.boundBankSync);

        // Initial state
        const pt = this.fPaymentTypeTarget.value;
        if (pt === 'tarjeta') {
            this.showTab('tarjeta');
            this.populateCardFromHidden();
        } else {
            this.showTab('cuenta');
            const c = this.fCountryTarget.value;
            if (c) {
                this.country = c;
                this.countrySelectTarget.value = c;
                this.renderCountryFields(c);
                this.populateBankFromHidden(c);
            }
        }
    }

    disconnect(): void {
        this.bankFieldsContainerTarget.removeEventListener('input',  this.boundBankSync);
        this.bankFieldsContainerTarget.removeEventListener('change', this.boundBankSync);
    }

    // ─── Tabs ─────────────────────────────────────────────────────────────────

    selectCuenta(): void { this.showTab('cuenta'); }
    selectTarjeta(): void { this.showTab('tarjeta'); }

    beforeSubmit(): void {
        if (this.fPaymentTypeTarget.value === 'tarjeta') {
            this.onCardNumberInput();
            this.onCardNumberConfirmInput();
            this.onCardExpiryInput();
            this.onCardCvvInput();
            this.onCardHolderInput();
            return;
        }

        this.syncBankToHidden();
    }

    private showTab(tab: 'cuenta' | 'tarjeta'): void {
        const isTarjeta = tab === 'tarjeta';

        // Use inline style — avoids any CSS specificity conflicts with d-none
        this.tarjetaSectionTarget.style.display = isTarjeta ? '' : 'none';
        this.cuentaSectionTarget.style.display  = isTarjeta ? 'none' : '';

        this.tabTarjetaTarget.classList.toggle('active', isTarjeta);
        this.tabCuentaTarget.classList.toggle('active',  !isTarjeta);

        // Sync hidden field immediately (reactive)
        this.fPaymentTypeTarget.value = isTarjeta ? 'tarjeta' : 'cuenta_bancaria';
    }

    // ─── Card section ──────────────────────────────────────────────────────────

    onCardNumberInput(): void {
        const isAmex = this.brand === 'Amex';
        const raw    = this.cardNumberInputTarget.value.replace(/\D/g, '').slice(0, isAmex ? 15 : 19);
        this.cardNumberInputTarget.value = formatCard(raw, isAmex);

        const found  = CARD_BRANDS.find(([re]) => re.test(raw));
        this.brand   = found ? found[1] : '';
        this.cardPreviewBrandTarget.textContent  = this.brand;

        const len = isAmex ? 15 : 16;
        const pad = raw.padEnd(len, '•');
        this.cardPreviewNumberTarget.textContent = isAmex
            ? pad.slice(0, 4) + ' ' + pad.slice(4, 10) + ' ' + pad.slice(10)
            : pad.replace(/(.{4})/g, '$1 ').trim();

        // Sync hidden immediately
        this.fCardNumberTarget.value = raw;
        this.fCardBrandTarget.value  = this.brand;

        // Check matching
        const rawConfirm = this.fCardNumberConfirmTarget.value;
        if (raw.length >= 13 || rawConfirm.length >= 13) {
            const matches = raw === rawConfirm;
            this.cardLuhnStatusTarget.textContent = matches
                ? (luhn(raw) ? '✓ Número válido (Luhn OK)' : '✗ Número inválido')
                : '✗ Los números de tarjeta no coinciden';
            this.cardLuhnStatusTarget.className = 'small mt-1 ' + (matches && luhn(raw) ? 'text-success' : 'text-danger');
        } else {
            this.cardLuhnStatusTarget.textContent = '';
        }
    }

    onCardNumberConfirmInput(): void {
        const isAmex = this.brand === 'Amex';
        const rawConfirm = this.cardNumberConfirmInputTarget.value.replace(/\D/g, '').slice(0, isAmex ? 15 : 19);
        this.cardNumberConfirmInputTarget.value = formatCard(rawConfirm, isAmex);
        this.fCardNumberConfirmTarget.value = rawConfirm;

        const raw = this.fCardNumberTarget.value;
        if (rawConfirm.length >= 13 || raw.length >= 13) {
            const matches = raw === rawConfirm;
            this.cardLuhnStatusTarget.textContent = matches
                ? (luhn(raw) ? '✓ Número válido (Luhn OK)' : '✗ Número inválido')
                : '✗ Los números de tarjeta no coinciden';
            this.cardLuhnStatusTarget.className = 'small mt-1 ' + (matches && luhn(raw) ? 'text-success' : 'text-danger');
        } else {
            this.cardLuhnStatusTarget.textContent = '';
        }
    }

    onCardExpiryInput(): void {
        let v = this.cardExpiryInputTarget.value.replace(/\D/g, '').slice(0, 4);
        if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
        this.cardExpiryInputTarget.value = v;
        this.fCardExpiryTarget.value = v;
    }

    onCardHolderInput(): void {
        this.cardHolderInputTarget.value = this.cardHolderInputTarget.value.toUpperCase();
        this.cardPreviewHolderTarget.textContent = this.cardHolderInputTarget.value || '••••••••';
        this.fCardHolderTarget.value = this.cardHolderInputTarget.value;
    }

    onCardCvvInput(): void {
        this.fCardCvvTarget.value = this.cardCvvInputTarget.value;
    }

    validateCard(): void {
        const raw = this.fCardNumberTarget.value.replace(/\D/g, '');
        const ok  = luhn(raw);
        const btn = this.validateCardBtnTarget;
        btn.textContent = ok ? '✓ Tarjeta válida' : '✗ Número inválido';
        btn.className   = ok ? 'btn btn-success w-100 mb-3' : 'btn btn-danger w-100 mb-3';
        setTimeout(() => {
            btn.textContent = '✓ Validar tarjeta';
            btn.className   = 'btn btn-outline-success w-100 mb-3';
        }, 2500);
    }

    private populateCardFromHidden(): void {
        const num    = this.fCardNumberTarget.value;
        const confirm = this.fCardNumberConfirmTarget.value;
        const expiry = this.fCardExpiryTarget.value;
        const cvv    = this.fCardCvvTarget.value;
        const holder = this.fCardHolderTarget.value;
        if (num) {
            this.cardNumberInputTarget.value = formatCard(num.replace(/\D/g, ''), false);
            this.onCardNumberInput();
        }
        if (confirm) {
            this.cardNumberConfirmInputTarget.value = formatCard(confirm.replace(/\D/g, ''), false);
        }
        if (expiry) { this.cardExpiryInputTarget.value = expiry; }
        if (cvv)    { this.cardCvvInputTarget.value    = cvv; }
        if (holder) {
            this.cardHolderInputTarget.value         = holder;
            this.cardPreviewHolderTarget.textContent = holder;
        }
    }

    // ─── Bank section ──────────────────────────────────────────────────────────

    onCountryChange(): void {
        this.country = this.countrySelectTarget.value;
        this.fCountryTarget.value = this.country;
        this.renderCountryFields(this.country);
    }

    private renderCountryFields(country: string): void {
        const cfg = COUNTRIES[country];
        if (!cfg) {
            this.bankFieldsContainerTarget.innerHTML = '';
            this.countryHintTarget.style.display    = 'none';
            this.validateAccountBtnTarget.style.display = 'none';
            return;
        }

        this.countryHintTarget.textContent       = 'ⓘ ' + cfg.hint;
        this.countryHintTarget.style.display     = '';
        this.validateAccountBtnTarget.style.display = cfg.validate ? '' : 'none';

        // Render fields
        let html = '';
        const fields = cfg.fields;
        let i = 0;
        while (i < fields.length) {
            const f    = fields[i];
            const next = fields[i + 1];
            // Two short numeric fields side by side
            if (next && f.maxlength && next.maxlength && f.maxlength <= 6 && next.maxlength <= 6) {
                html += `<div class="row mb-0"><div class="col-6">${this.fieldHtml(f, false)}</div><div class="col-6">${this.fieldHtml(next, false)}</div></div>`;
                i += 2;
            } else {
                html += this.fieldHtml(f);
                i++;
            }
        }

        this.bankFieldsContainerTarget.innerHTML = html;
    }

    validateAccount(): void {
        const cfg = COUNTRIES[this.country];
        if (!cfg?.validate) return;
        const result = cfg.validate(this.getBankValues());
        const btn    = this.validateAccountBtnTarget;
        btn.textContent = result.msg;
        btn.className   = result.ok ? 'btn btn-success w-100 mb-3' : 'btn btn-danger w-100 mb-3';
        setTimeout(() => {
            btn.textContent = '✓ Validar cuenta';
            btn.className   = 'btn btn-outline-success w-100 mb-3';
        }, 2500);
    }

    private getBankValues(): Record<string, string> {
        const vals: Record<string, string> = {};
        this.bankFieldsContainerTarget
            .querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-bank-key]')
            .forEach(el => { vals[el.dataset.bankKey!] = el.value; });
        return vals;
    }

    /** Reactive sync: fires whenever any bank input/select changes */
    private syncBankToHidden(): void {
        const cfg = COUNTRIES[this.country];
        if (!cfg) return;
        const vals = this.getBankValues();
        const mapped = cfg.toEntity(vals);
        this.fBankNameTarget.value      = mapped.bankName      ?? '';
        this.fAccountHolderTarget.value = mapped.accountHolder ?? '';
        this.fAccountNumberTarget.value = mapped.accountNumber ?? '';
        this.fAccountTypeTarget.value   = mapped.accountType   ?? '';
        this.fSwiftBicTarget.value      = mapped.swiftBic      ?? '';
        this.fCountryTarget.value       = this.country;

        // Sync confirm field value
        let confirmVal = '';
        if (vals.clabeConfirm) confirmVal = vals.clabeConfirm;
        else if (vals.accountConfirm) confirmVal = vals.accountConfirm;
        else if (vals.ibanConfirm) confirmVal = vals.ibanConfirm;
        else if (vals.cbuConfirm) confirmVal = vals.cbuConfirm;
        else if (vals.cciConfirm) confirmVal = vals.cciConfirm;
        this.fAccountNumberConfirmTarget.value = confirmVal;
    }

    private populateBankFromHidden(country: string): void {
        const cfg = COUNTRIES[country];
        if (!cfg) return;
        const entity = {
            bankName:      this.fBankNameTarget.value      || null,
            accountHolder: this.fAccountHolderTarget.value || null,
            accountNumber: this.fAccountNumberTarget.value || null,
            accountType:   this.fAccountTypeTarget.value   || null,
            swiftBic:      this.fSwiftBicTarget.value      || null,
        };
        const uiVals = cfg.fromEntity(entity);
        this.bankFieldsContainerTarget
            .querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-bank-key]')
            .forEach(el => {
                const key = el.dataset.bankKey!;
                if (uiVals[key] !== undefined) el.value = uiVals[key];
            });
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    private fieldHtml(f: BankField, addMarginBottom = true): string {
        const id      = `dbf_${f.key}`;
        const mb      = addMarginBottom ? ' mb-3' : ' mb-3';
        const reqMark = f.required ? ' <span class="text-danger">*</span>' : '';
        const hintTxt = f.hint ? ` <small class="text-muted fw-normal">${this.esc(f.hint)}</small>` : '';

        if (f.type === 'select' && f.options) {
            const opts = [{ value: '', label: '— Selecciona —' }, ...f.options]
                .map(o => `<option value="${this.esc(o.value)}">${this.esc(o.label)}</option>`)
                .join('');
            return `<div class="${mb}">
                <label class="form-label fw-semibold small mb-1" for="${id}">${this.esc(f.label)}${reqMark}</label>
                <select id="${id}" class="form-select form-select-sm" data-bank-key="${f.key}"${f.required ? ' required' : ''}>${opts}</select>
            </div>`;
        }
        return `<div class="${mb}">
            <label class="form-label fw-semibold small mb-1" for="${id}">${this.esc(f.label)}${reqMark}${hintTxt}</label>
            <input type="text" id="${id}" class="form-control form-control-sm"
                data-bank-key="${f.key}"
                ${f.placeholder  ? `placeholder="${this.esc(f.placeholder)}"` : ''}
                ${f.maxlength   ? `maxlength="${f.maxlength}"` : ''}
                ${f.inputmode   ? `inputmode="${f.inputmode}"` : ''}
                ${f.required    ? 'required' : ''}>
        </div>`;
    }

    private esc(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }
}
